// @ts-nocheck
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CaretDown } from '@phosphor-icons/react';
import { useState } from 'react';
import { EmbedSignDocument } from '@documenso/embed-react';
import pdf from '../../../../assets/images/pdf.png';
import PassengersCheckBoxForm from '../PassengersCheckBoxForm/PassengersCheckBoxForm';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  InputLabel,
  FormHelperText,
  Modal
} from '@mui/material';
import { MuiTelInput } from 'mui-tel-input';
import { XGInput, XGDatePicker, XGAutocomplete, XGButton } from 'xg-cl';
import XGTextarea from '../../../ProseTextarea/ProseTextarea';
import * as PropTypes from 'prop-types';
import './BookingPassengerForm.css';
import PassengersRadioForm from '../PassengersRadioForm/PassengersRadioForm';
import LineItemAttachmentManager from '../../../LineItem/LineItemAttachmentManager/LineItemAttachmentManager';
import {
  DATE,
  TEXTAREA,
  CHECKBOX,
  RADIOBUTTON,
  BOOLEAN,
  PHONE
} from '../../../../constants/passengers';
import {
  maxPassengerFieldUploadFiles,
  maxPassengerFieldUploadSize,
  supportedAttachmentAccept,
  supportedAttachmentExtensions,
  supportedAttachmentMimeTypes
} from '../../../../constants/files';
import { convertInputDate } from '../../../../utils/dateUtility';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import standardFields from '../../../../constants/standardFields';
import {
  getPassengerFieldUploadFiles,
  hasPreviouslyProvidedPassengerFieldFiles,
  isFileUploadPassengerFieldType,
  isSignableDocumentPassengerFieldType
} from '../../../../utils/passengerFieldTypes';
import { bytesToMiB } from '../../../../utils/fileHelper';
import globalConfig from '../../../../config/globalConfig';
import {
  generatePassengerSignedDocumentByPassengerKey,
  generatePassengerSignedDocumentByPassengerId
} from '../../../../services/booking/booking';
import { useDocumentSocket } from '../../../../hooks/useDocumentSocket';
import { ErrorToast } from '../../../../utils/alerts';
import {
  mapSignedDocumentGenerateResponse,
  selectPassengerSignedDocumentGenerateEndpoint
} from '../../../../utils/passengerWaiverSigning';

/**
 * Functional React component for rendering a custom  passenger form component .
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {string} [props.id] - The ID for the passenger form component.
 * @param {Object} form - The form object.
 * @param {number} sequence - The sequence of the form.
 * @param {Function} [props.handleOnChange] - The function to handle the onChange event.
 *
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the passenger form component.
 */

const BookingPassengerForm = ({
  countries,
  id,
  form,
  sequence,
  handleOnChange,
  handleOnFileChange,
  handleOnFileError,
  passengerFieldFiles = {},
  ...rest
}) => {
  const { t } = useTranslation(['trip', 'common', 'errors', 'passengers']);
  const { enablePassengerFieldUploads, enablePaxWaivers } = useFlags();
  const { formId, title, fields = [] } = form;
  const color = useSelector((state) => state?.color || {});
  const [signableDocumentModal, setSignableDocumentModal] = useState(null);
  const [signingFieldId, setSigningFieldId] = useState(null);
  const [signableDocumentErrors, setSignableDocumentErrors] = useState({});
  const [signedAtByField, setSignedAtByField] = useState({});

  const checkDescriptionForSystemFields = (
    title,
    isrequired,
    fieldDescription
  ) => {
    const systemField = standardFields.find((field) => field.label === title);
    if (systemField) {
      return `${title}${isrequired ? '*' : ''}`;
    } else {
      return `${title}${isrequired ? '*' : ''}${fieldDescription}`;
    }
  };

  const getDate = (value) => {
    const newValue = Array.isArray(value) ? value[0] : value;
    if (!newValue) return null;
    const date = new Date(newValue);
    const offsetInMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + offsetInMs);
  };

  const getFieldValueByTitle = (fieldTitle = '') => {
    const matchedField = fields.find((field) => field?.title === fieldTitle);
    const fieldValue = matchedField?.value;

    if (typeof fieldValue === 'string') {
      return fieldValue.trim();
    }

    return '';
  };

  const getSignedAtForField = (field = {}) => {
    return (
      signedAtByField[field?.fieldId] ||
      field?.signedDateTime ||
      field?.signeddatetime ||
      field?.bookingpassengersigneddocument?.signedDateTime ||
      field?.bookingpassengersigneddocument?.signeddatetime ||
      field?.bookingPassengerSignedDocument?.signedDateTime ||
      field?.bookingPassengerSignedDocument?.signeddatetime ||
      null
    );
  };

  const getSignedDateLabel = (field = {}) => {
    const signedAt = getSignedAtForField(field);

    if (!signedAt) {
      return '';
    }

    const signedDate = new Date(signedAt);

    if (Number.isNaN(signedDate.getTime())) {
      return '';
    }

    return format(signedDate, t('common:dateFormat'));
  };

  const handleSignableDocumentSign = async (field = {}) => {
    const bookingpassengerid =
      field?.bookingpassengerid ||
      field?.bookingPassengerId ||
      form?.bookingpassengerid ||
      form?.bookingPassengerId;
    const trippassengerfieldid = field?.trippassengerfieldid;
    const email = getFieldValueByTitle('Email Address');
    const name = [
      getFieldValueByTitle('First Name'),
      getFieldValueByTitle('Last Name')
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    const resolvedPassengerKey = form?.uuid || null;

    if (!email || !name) {
      setSignableDocumentErrors((prev) => ({
        ...prev,
        [field?.fieldId]: t('passengers:signerDetailsRequired')
      }));
      return;
    }

    if (!trippassengerfieldid) {
      setSignableDocumentErrors((prev) => ({
        ...prev,
        [field?.fieldId]: t(
          'errors:submissionFailed',
          'Submission failed. Please try again.'
        )
      }));
      return;
    }

    setSigningFieldId(field?.fieldId);
    setSignableDocumentErrors((prev) => ({
      ...prev,
      [field?.fieldId]: null
    }));

    try {
      const endpoint = selectPassengerSignedDocumentGenerateEndpoint({
        bookingpassengerid,
        passengerkey: resolvedPassengerKey
      });

      if (!endpoint) {
        setSignableDocumentErrors((prev) => ({
          ...prev,
          [field?.fieldId]: t(
            'errors:submissionFailed',
            'Submission failed. Please try again.'
          )
        }));
        return;
      }

      const requestPayload = {
        trippassengerfieldid,
        email,
        name
      };

      const response =
        endpoint === 'by-id'
          ? await generatePassengerSignedDocumentByPassengerId(
              bookingpassengerid,
              resolvedPassengerKey,
              requestPayload
            )
          : await generatePassengerSignedDocumentByPassengerKey(
              resolvedPassengerKey,
              requestPayload
            );

      const { documentState, documentReady, token, externalSignedDocumentId } =
        mapSignedDocumentGenerateResponse(response);

      if (response?.error || response?.data?.error || !documentState) {
        ErrorToast(
          t('errors:submissionFailed', 'Submission failed. Please try again.')
        );
        setSignableDocumentErrors((prev) => ({
          ...prev,
          [field?.fieldId]: t(
            'errors:submissionFailed',
            'Submission failed. Please try again.'
          )
        }));
        return;
      }

      if (documentReady) {
        handleOnChange(formId, field?.fieldId, 'SIGNED');
        setSignedAtByField((prev) => ({
          ...prev,
          [field?.fieldId]: new Date().toISOString()
        }));
        return;
      }

      if (token) {
        setSignableDocumentModal({
          fieldId: field?.fieldId,
          token,
          externalSignedDocumentId
        });
        return;
      }

      ErrorToast(
        t('errors:submissionFailed', 'Submission failed. Please try again.')
      );
    } catch (error) {
      ErrorToast(
        t('errors:submissionFailed', 'Submission failed. Please try again.')
      );
      setSignableDocumentErrors((prev) => ({
        ...prev,
        [field?.fieldId]: t(
          'errors:submissionFailed',
          'Submission failed. Please try again.'
        )
      }));
    } finally {
      setSigningFieldId(null);
    }
  };

  const handleSignableDocumentCompleted = () => {
    if (signableDocumentModal?.fieldId) {
      handleOnChange(formId, signableDocumentModal.fieldId, 'SIGNED');
      setSignedAtByField((prev) => ({
        ...prev,
        [signableDocumentModal.fieldId]: new Date().toISOString()
      }));
      setSignableDocumentErrors((prev) => ({
        ...prev,
        [signableDocumentModal.fieldId]: null
      }));
    }

    setSignableDocumentModal(null);
  };

  useDocumentSocket(
    signableDocumentModal?.externalSignedDocumentId,
    (event) => {
      if (event === 'DOCUMENT_SIGNED') {
        handleSignableDocumentCompleted();
      }
    }
  );

  return (
    <Box id={id} className='passenger-form__container' {...rest}>
      <Accordion className='passenger-form__accordion' defaultExpanded>
        <AccordionSummary
          expandIcon={<CaretDown size={24} weight='bold' />}
          aria-controls='passenger-form-panel-content'
          id='passenger-form-panel-header'
          className='passenger-form__accordion-summary'
        >
          <span className='text-md-semibold passenger-form__header'>
            <span
              className='passenger-form__step'
              style={{
                backgroundColor:
                  color?.highlightColor || 'var(--primary-600, #1361d8)'
              }}
            >
              {sequence}
            </span>
            {t('passengerInformation')} - {title || ''}
          </span>
        </AccordionSummary>
        <AccordionDetails className='passenger-form__accordion-details'>
          <Grid container className='passenger-form__grid'>
            {fields &&
              fields.map((field, index) => {
                const { fieldtype } = field;
                const fieldDescription = field?.description
                  ? ` - ${field?.description.trim()}`
                  : '';
                const label = checkDescriptionForSystemFields(
                  field?.title,
                  field?.isrequired,
                  fieldDescription
                );

                if (
                  enablePassengerFieldUploads &&
                  isFileUploadPassengerFieldType(fieldtype)
                ) {
                  const uploadedFiles = getPassengerFieldUploadFiles(
                    passengerFieldFiles,
                    formId,
                    field?.trippassengerfieldid
                  );

                  return (
                    <Box
                      className='w-100 passenger-form__input-container'
                      key={index}
                    >
                      <InputLabel
                        className='xg-input-label'
                        htmlFor={`passenger-${field?.key}-input-${id}`}
                      >
                        {label}
                      </InputLabel>
                      <LineItemAttachmentManager
                        attachments={uploadedFiles}
                        onChange={(files) =>
                          handleOnFileChange(formId, field, files)
                        }
                        onError={(error) =>
                          handleOnFileError(formId, field, error)
                        }
                        maxAttachments={maxPassengerFieldUploadFiles}
                        maxSize={maxPassengerFieldUploadSize}
                        label={t('common:addAttachment')}
                        uploadLabel={t('common:uploadAttachment')}
                        accept={supportedAttachmentAccept}
                        acceptedMimeTypes={supportedAttachmentMimeTypes}
                        acceptedExtensions={supportedAttachmentExtensions}
                        error={field?.error}
                        hint={t('common:maxFileSize', {
                          maxFileSize: bytesToMiB(maxPassengerFieldUploadSize)
                        })}
                        previouslyProvided={hasPreviouslyProvidedPassengerFieldFiles(
                          field
                        )}
                      />
                    </Box>
                  );
                }

                if (
                  enablePaxWaivers &&
                  isSignableDocumentPassengerFieldType(fieldtype)
                ) {
                  const isSigned = Boolean(field?.value);
                  const fieldError =
                    signableDocumentErrors[field?.fieldId] || field?.error;
                  const isSigning = signingFieldId === field?.fieldId;
                  const signedDate = getSignedDateLabel(field);

                  return (
                    <Box
                      className='w-100 passenger-form__input-container'
                      key={index}
                    >
                      <Box
                        className={`passenger-waiver-field ${isSigned ? 'passenger-waiver-field--signed' : ''}`}
                      >
                        <Box className='passenger-waiver-field__header'>
                          <Box>
                            <InputLabel
                              className='xg-input-label passenger-waiver-field__label'
                              htmlFor={`passenger-signable-document-${field?.key}-input-${id}`}
                            >
                              {field?.title}
                              {field?.isrequired && <span> *</span>}
                            </InputLabel>
                            {field?.description && (
                              <span className='passenger-waiver-field__description'>
                                {field.description}
                              </span>
                            )}
                          </Box>
                        </Box>

                        {!isSigned && (
                          <XGButton
                            className='passenger-waiver-field__action-btn'
                            id={`passenger-signable-document-${field?.key}-input-${id}`}
                            type='secondary'
                            variant='outlined'
                            size='medium'
                            startIcon={<img src={pdf} alt='PDF' />}
                            text={
                              isSigning
                                ? t('passengers:documentInProgress')
                                : t('passengers:clickToSignPdf')
                            }
                            disabled={isSigning}
                            onClick={() => handleSignableDocumentSign(field)}
                          />
                        )}

                        {isSigned && (
                          <Box className='passenger-waiver-field__signed-row'>
                            <span className='passenger-waiver-field__signed-icon'>
                              ✓
                            </span>
                            <span className='passenger-waiver-field__signed-label'>
                              {t('common:signed')}
                            </span>
                            {signedDate && (
                              <span className='passenger-waiver-field__signed-date'>
                                {signedDate}
                              </span>
                            )}
                          </Box>
                        )}

                        {fieldError && (
                          <FormHelperText className='input-form-helper-text--error'>
                            {fieldError}
                          </FormHelperText>
                        )}
                      </Box>
                    </Box>
                  );
                }

                return fieldtype?.key === RADIOBUTTON ||
                  fieldtype?.key === BOOLEAN ? (
                  <PassengersRadioForm
                    key={index}
                    formId={id}
                    field={field}
                    handleChange={(value) => {
                      handleOnChange(formId, field?.fieldId, value);
                    }}
                  />
                ) : fieldtype?.key === PHONE ? (
                  <Box
                    className='w-100 passenger-form__input-container'
                    key={index}
                  >
                    <InputLabel
                      className='xg-input-label phone-input-label'
                      htmlFor={`passenger-${field?.key}-input-${id}`}
                    >
                      {label}
                    </InputLabel>
                    <MuiTelInput
                      className={`phone-input ${field?.error ? ' passenger-form__phone-input-error ' : ''}`}
                      value={field?.value}
                      name={field?.key}
                      id={`passenger-${field?.key}-input-${id}`}
                      onChange={(value) =>
                        handleOnChange(formId, field?.fieldId, value)
                      }
                      defaultCountry='US'
                      inputProps={{
                        autoComplete: 'off',
                        inputMode: 'numeric',
                        pattern: '[0-9]*'
                      }}
                    />
                    {field?.error && (
                      <FormHelperText className='input-form-helper-text--error'>
                        {field?.error}
                      </FormHelperText>
                    )}
                  </Box>
                ) : fieldtype?.key === CHECKBOX ? (
                  <PassengersCheckBoxForm
                    key={index}
                    id={`passenger-${field?.key}-input-${id}`}
                    field={field}
                    selectedOptions={field?.value}
                    formId={formId}
                    handleOnChange={handleOnChange}
                  />
                ) : fieldtype?.key === TEXTAREA ? (
                  <Box
                    className='w-100 passenger-form__input-container'
                    key={index}
                  >
                    <XGTextarea
                      placeholder={`${t('common:enter')} ${field?.title?.toLowerCase()}`}
                      label={label}
                      id={`passenger-${field?.key}-input-${id}`}
                      type='text'
                      name={field?.key}
                      value={field?.value}
                      onChange={(evt) =>
                        handleOnChange(formId, field?.fieldId, evt.target.value)
                      }
                      inputProps={{
                        maxLength: 1000,
                        autoComplete: 'off'
                      }}
                      error={field?.error ? true : false}
                      helpertext={field?.error}
                      minRows={3}
                    />

                    <>
                      {field?.error && (
                        <div className='input-helper-text-container '>
                          <FormHelperText className='input-form-helper-text--error'>
                            {field?.error}
                          </FormHelperText>{' '}
                        </div>
                      )}
                    </>

                    <span className='input-form-length-hint input-form-length-hint--compact'>
                      {field?.value?.length || 0}/1000
                    </span>
                  </Box>
                ) : fieldtype?.key === DATE ? (
                  <Box
                    className='w-100 passenger-form__input-container'
                    key={index}
                  >
                    <XGDatePicker
                      className={`${field?.error ? ' passenger-form__input-error ' : ''}`}
                      style={{ width: '100%' }}
                      format={`${t('common:dateFormat')}`}
                      id={`passenger-${field?.key}-input-${id}`}
                      name={field?.key}
                      label={checkDescriptionForSystemFields(
                        field?.title,
                        field?.isrequired,
                        fieldDescription
                      )}
                      autoComplete='off'
                      value={field?.value ? getDate(field?.value) : null}
                      onChange={(selectedDate) => {
                        // Have to only push the date if the year is after 1900 - XGE-1803
                        //  Dates and times before 1850 have a weird offset of (for example) 0456 instead of 0500
                        //  Since onChange triggers on each character typed, it goes through years 1, 19, 198, and 1986 when typing "1986".
                        //  We don't want to handle the change until the year is one that has normal offset.
                        if (selectedDate.getFullYear() > 1900) {
                          handleOnChange(
                            formId,
                            field?.fieldId,
                            convertInputDate(selectedDate).toISOString()
                          );
                        }
                      }}
                      error={field?.error ? true : false}
                      hint={field?.error}
                    />
                  </Box>
                ) : fieldtype?.key === 'country' ? ( //TODO: Define this in the backend
                  <Box
                    className='w-100 passenger-form__input-container'
                    key={index}
                  >
                    <XGAutocomplete
                      className={`autocomplete ${field?.error ? ' passenger-form__phone-input-error ' : ''}`}
                      disableClearable={false}
                      placeholder={t('searchCountry')}
                      name={field?.key}
                      label={checkDescriptionForSystemFields(
                        field?.title,
                        field?.isrequired,
                        fieldDescription
                      )}
                      options={countries}
                      isOptionEqualToValue={(option, value) =>
                        option.title === value?.title
                      }
                      onChange={(event, newValue) => {
                        const { countrycode } = newValue || {};
                        handleOnChange(formId, field?.fieldId, countrycode);
                      }}
                    />
                    {field?.error && (
                      <FormHelperText className='input-form-helper-text--error'>
                        {field?.error}
                      </FormHelperText>
                    )}
                  </Box>
                ) : (
                  <Box
                    className='w-100 passenger-form__input-container'
                    key={index}
                  >
                    <XGInput
                      label={checkDescriptionForSystemFields(
                        field?.title,
                        field?.isrequired,
                        fieldDescription
                      )}
                      name={field?.key}
                      placeholder={`${t('common:enter')} ${field?.title?.toLowerCase()}`}
                      id={`passenger-${field?.key}-input-${id}`}
                      type={'text'}
                      value={field?.value}
                      onChange={(evt) =>
                        handleOnChange(
                          formId,
                          field?.fieldId,
                          evt.target.value,
                          fieldtype?.key
                        )
                      }
                      inputProps={{
                        maxLength: 100,
                        autoComplete: 'off'
                      }}
                    />

                    <>
                      {field?.error && (
                        <div className='input-helper-text-container'>
                          <FormHelperText className='input-form-helper-text--error'>
                            {field?.error}
                          </FormHelperText>
                        </div>
                      )}
                    </>

                    <span className='input-form-length-hint input-form-length-hint--compact'>
                      {field?.value?.length || 0}/100
                    </span>
                  </Box>
                );
              })}
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Modal
        open={Boolean(signableDocumentModal?.token)}
        onClose={() => setSignableDocumentModal(null)}
        aria-labelledby='booking-passenger-waiver-signing-modal'
      >
        <Box className='passenger-waiver-signing-modal'>
          {signableDocumentModal?.token && (
            <EmbedSignDocument
              host={globalConfig.eSignatureHost}
              token={signableDocumentModal.token}
              lockName
              onDocumentCompleted={handleSignableDocumentCompleted}
              className='passenger-waiver-signing-modal__embed'
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

BookingPassengerForm.propTypes = {
  handleOnChange: PropTypes.func,
  handleOnFileChange: PropTypes.func,
  handleOnFileError: PropTypes.func,
  form: PropTypes.shape({
    formId: PropTypes.string,
    title: PropTypes.string,
    fields: PropTypes.array
  }),
  id: PropTypes.string.isRequired,
  sequence: PropTypes.number,
  countries: PropTypes.array,
  passengerFieldFiles: PropTypes.object
};
export default BookingPassengerForm;
